import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../../api/api";
import toast from "react-hot-toast";
import { ArrowLeft, Save, ExternalLink, Trash2 } from "lucide-react";

const statusColors = {
  submitted: "bg-blue-50 text-blue-700",
  under_review: "bg-yellow-50 text-yellow-700",
  more_information_required: "bg-orange-50 text-orange-700",
  assigned: "bg-indigo-50 text-indigo-700",
  investigation_ongoing: "bg-purple-50 text-purple-700",
  resolved: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
  escalated: "bg-rose-50 text-rose-700",
  closed: "bg-slate-100 text-slate-700",
};

const departments = [
  "Licensing and Permits",
  "Compliance and Enforcement",
  "Legal Services",
  "Investment Services",
  "Zone Operations",
  "ICT and Portal Support",
  "Public Affairs",
  "Human Resources",
  "Finance and Tariff Review",
  "Procurement",
  "SERVICOM or Customer Service Desk",
  "Executive Management Escalation",
];

const Info = ({ label, value }) => (
  <div className="border border-slate-100 rounded-2xl p-4">
    <p className="text-xs uppercase tracking-wider text-slate-400 font-black">
      {label}
    </p>
    <p className="mt-1 font-bold text-slate-800 break-words">
      {value === true ? "Yes" : value === false ? "No" : value || "—"}
    </p>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
    <h2 className="text-xl font-black text-[#06183f] mb-5">{title}</h2>
    {children}
  </div>
);

const AdminComplaintDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    status: "submitted",
    assigned_department: "",
    assigned_officer: "",
    response_note: "",
    action_report: "",
    resolution_summary: "",
    admin_notes: "",
  });

  const loadComplaint = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/admin/complaints/${id}`);
      setComplaint(res.data);

      setForm({
        status: res.data.status || "submitted",
        assigned_department: res.data.assigned_department || "",
        assigned_officer: res.data.assigned_officer || "",
        response_note: res.data.response_note || "",
        action_report: res.data.action_report || "",
        resolution_summary: res.data.resolution_summary || "",
        admin_notes: res.data.admin_notes || "",
      });
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load complaint");
      navigate("/admin/complaints");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComplaint();
  }, [id]);

  const updateComplaint = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      await api.put(`/admin/complaints/${id}`, form);

      toast.success("Complaint updated");
      loadComplaint();
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to update complaint");
    } finally {
      setSaving(false);
    }
  };

  const deleteComplaint = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this complaint?"
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/admin/complaints/${id}`);
      toast.success("Complaint deleted");
      navigate("/admin/complaints");
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to delete complaint");
    }
  };

  if (loading || !complaint) {
    return <p>Loading complaint...</p>;
  }

  return (
    <div>
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
        <div>
          <Link
            to="/admin/complaints"
            className="inline-flex items-center gap-2 text-[#06183f] font-bold mb-3"
          >
            <ArrowLeft size={18} />
            Back to complaints
          </Link>

          <h1 className="text-3xl font-black text-[#06183f]">
            Complaint Details
          </h1>

          <p className="text-slate-500 mt-1">
            Reference Number:{" "}
            <span className="font-black text-slate-800">
              {complaint.tracking_number}
            </span>
          </p>
        </div>

        <button
          onClick={deleteComplaint}
          className="px-5 py-3 rounded-2xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white font-black flex items-center gap-2"
        >
          <Trash2 size={18} />
          Delete
        </button>
      </div>

      <div className="grid xl:grid-cols-[1fr_430px] gap-6">
        <div className="space-y-6">
          <Section title="Complaint Overview">
            <div className="flex items-center justify-between gap-4 mb-6">
              <span
                className={`px-4 py-2 rounded-full text-xs font-black uppercase ${
                  statusColors[complaint.status] ||
                  "bg-slate-100 text-slate-700"
                }`}
              >
                {complaint.status?.replaceAll("_", " ")}
              </span>

              {complaint.anonymous && (
                <span className="px-4 py-2 rounded-full text-xs font-black uppercase bg-amber-50 text-amber-700">
                  Anonymous
                </span>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Info label="Subject" value={complaint.subject} />
              <Info label="Complaint Type" value={complaint.complaint_type} />
              <Info
                label="Complainant Category"
                value={complaint.complainant_category}
              />
              <Info label="Free Zone" value={complaint.free_zone} />
              <Info label="Date Submitted" value={complaint.created_at} />
              <Info label="Last Updated" value={complaint.updated_at} />
            </div>

            <div className="mt-5 border border-slate-100 rounded-2xl p-5">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-black">
                Detailed Description
              </p>
              <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-wrap">
                {complaint.description}
              </p>
            </div>
          </Section>

          <Section title="Complainant Information">
            <div className="grid md:grid-cols-2 gap-4">
              <Info label="Full Name" value={complaint.full_name} />
              <Info label="Organisation" value={complaint.organisation} />
              <Info label="Designation" value={complaint.designation} />
              <Info label="Phone" value={complaint.phone} />
              <Info label="Email" value={complaint.email} />
              <Info label="Contact Address" value={complaint.contact_address} />
              <Info
                label="Preferred Contact Method"
                value={complaint.preferred_contact_method}
              />
              <Info
                label="Submitting On Behalf"
                value={complaint.submitting_on_behalf}
              />
              <Info
                label="Licensed Operator / Enterprise"
                value={complaint.is_licensed_operator}
              />
              <Info label="Licence Number" value={complaint.licence_number} />
            </div>
          </Section>

          <Section title="Free Zone / Organisation Involved">
            <div className="grid md:grid-cols-2 gap-4">
              <Info label="Free Zone" value={complaint.free_zone} />
              <Info
                label="Company / Operator Involved"
                value={complaint.company_involved}
              />
              <Info
                label="Location Within Zone"
                value={complaint.location_within_zone}
              />
              <Info
                label="Facility / Office / Unit"
                value={complaint.facility_or_unit}
              />
              <Info label="Incident Date" value={complaint.incident_date} />
              <Info label="Incident Time" value={complaint.incident_time} />
            </div>
          </Section>

          <Section title="Case Details">
            <div className="grid md:grid-cols-2 gap-4">
              <Info
                label="Persons / Departments Involved"
                value={complaint.persons_involved}
              />
              <Info
                label="Reported Before"
                value={complaint.reported_before}
              />
              <Info
                label="Previous Reference"
                value={complaint.previous_reference}
              />
              <Info label="Urgent" value={complaint.urgent} />
              <Info label="Financial Loss" value={complaint.financial_loss} />
              <Info label="Amount Involved" value={complaint.amount_involved} />
              <Info
                label="Affects Business Operations"
                value={complaint.affects_business_operations}
              />
            </div>

            <div className="mt-5 grid md:grid-cols-2 gap-4">
              <div className="border border-slate-100 rounded-2xl p-5">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-black">
                  Action Already Taken
                </p>
                <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {complaint.action_taken || "—"}
                </p>
              </div>

              <div className="border border-slate-100 rounded-2xl p-5">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-black">
                  Requested Outcome
                </p>
                <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {complaint.requested_outcome || "—"}
                </p>
              </div>
            </div>
          </Section>

          <Section title="Evidence Files">
            {complaint.evidence_files?.length > 0 ? (
              <div className="space-y-3">
                {complaint.evidence_files.map((file, index) => (
                  <a
                    key={file}
                    href={file}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between gap-4 border border-slate-100 rounded-2xl p-4 text-[#06183f] font-black hover:border-[#ff6600] hover:text-[#ff6600]"
                  >
                    Evidence File {index + 1}
                    <ExternalLink size={17} />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">No evidence uploaded.</p>
            )}
          </Section>

          {(complaint.action_report ||
            complaint.resolution_summary ||
            complaint.admin_notes) && (
            <Section title="Admin Case Notes">
              {complaint.action_report && (
                <div className="mb-5">
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-black">
                    Internal Action Report
                  </p>
                  <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {complaint.action_report}
                  </p>
                </div>
              )}

              {complaint.resolution_summary && (
                <div className="mb-5">
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-black">
                    Resolution Summary
                  </p>
                  <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {complaint.resolution_summary}
                  </p>
                </div>
              )}

              {complaint.admin_notes && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-black">
                    Admin Notes
                  </p>
                  <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {complaint.admin_notes}
                  </p>
                </div>
              )}
            </Section>
          )}
        </div>

        <form
          onSubmit={updateComplaint}
          className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm h-fit"
        >
          <h2 className="text-xl font-black text-[#06183f] mb-5">
            Manage Complaint
          </h2>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">Status</span>
            <select
              value={form.status}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, status: e.target.value }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] bg-white"
            >
              <option value="submitted">Submitted</option>
              <option value="under_review">Under Review</option>
              <option value="more_information_required">
                More Information Required
              </option>
              <option value="assigned">Assigned</option>
              <option value="investigation_ongoing">
                Investigation Ongoing
              </option>
              <option value="resolved">Resolved</option>
              <option value="rejected">Rejected</option>
              <option value="escalated">Escalated</option>
              <option value="closed">Closed</option>
            </select>
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">
              Assigned Department
            </span>
            <select
              value={form.assigned_department}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  assigned_department: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] bg-white"
            >
              <option value="">Select Department</option>
              {departments.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">
              Assigned Officer
            </span>
            <input
              value={form.assigned_officer}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  assigned_officer: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
              placeholder="Officer or unit name"
            />
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">
              Public Tracking Response
            </span>
            <textarea
              rows="4"
              value={form.response_note}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  response_note: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
              placeholder="This can show when the complainant tracks the complaint."
            />
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">
              Internal Action Report
            </span>
            <textarea
              rows="5"
              value={form.action_report}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  action_report: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
              placeholder="Internal investigation/action report."
            />
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">
              Resolution Summary
            </span>
            <textarea
              rows="4"
              value={form.resolution_summary}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  resolution_summary: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
              placeholder="Summary shown after resolution where appropriate."
            />
          </label>

          <label className="block mb-6">
            <span className="font-bold text-slate-700">
              Admin Notes
            </span>
            <textarea
              rows="4"
              value={form.admin_notes}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  admin_notes: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]"
              placeholder="Private internal notes."
            />
          </label>

          <button
            disabled={saving}
            className="w-full py-4 rounded-2xl bg-[#06183f] hover:bg-[#ff6600] text-white font-black flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Save size={18} />
            {saving ? "Saving..." : "Save Complaint Update"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminComplaintDetails;